import { useState, useEffect, useCallback } from 'react';
import supabase from '../supabase/supabase';

export const useDrops = (artistId) => {
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDrops = useCallback(async () => {
    if (!artistId) { setLoading(false); return; }
    setLoading(true);
    const { data, error: err } = await supabase
      .from('d2f_drops')
      .select('*')
      .eq('artist_id', artistId)
      .order('created_at', { ascending: false });
    setDrops(data || []);
    setError(err?.message || null);
    setLoading(false);
  }, [artistId]);

  useEffect(() => { fetchDrops(); }, [fetchDrops]);

  const createDrop = async (payload) => {
    const { data, error } = await supabase
      .from('d2f_drops')
      .insert({ ...payload, artist_id: artistId })
      .select().single();
    if (!error) await fetchDrops();
    return { data, error };
  };

  const updateDrop = async (id, updates) => {
    const { error } = await supabase
      .from('d2f_drops')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) await fetchDrops();
    return { error };
  };

  const deleteDrop = async (id) => {
    const { error } = await supabase.from('d2f_drops').delete().eq('id', id);
    if (!error) await fetchDrops();
    return { error };
  };

  const togglePublish = async (id, is_published) => {
    return updateDrop(id, { is_published });
  };

  return { drops, loading, error, refetch: fetchDrops, createDrop, updateDrop, deleteDrop, togglePublish };
};

export const useMerch = (artistId) => {
  const [merch, setMerch] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMerch = useCallback(async () => {
    if (!artistId) { setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from('d2f_merch_items')
      .select('*')
      .eq('artist_id', artistId)
      .order('created_at', { ascending: false });
    setMerch(data || []);
    setLoading(false);
  }, [artistId]);

  useEffect(() => { fetchMerch(); }, [fetchMerch]);

  const createMerch = async (payload) => {
    const { data, error } = await supabase
      .from('d2f_merch_items')
      .insert({ ...payload, artist_id: artistId })
      .select().single();
    if (!error) await fetchMerch();
    return { data, error };
  };

  const updateMerch = async (id, updates) => {
    const { error } = await supabase.from('d2f_merch_items').update(updates).eq('id', id);
    if (!error) await fetchMerch();
    return { error };
  };

  const deleteMerch = async (id) => {
    const { error } = await supabase.from('d2f_merch_items').delete().eq('id', id);
    if (!error) await fetchMerch();
    return { error };
  };

  return { merch, loading, refetch: fetchMerch, createMerch, updateMerch, deleteMerch };
};

export const useDropUnlocks = (userId) => {
  const [unlocked, setUnlocked] = useState(new Set());

  useEffect(() => {
    if (!userId) return;
    supabase
      .from('d2f_drop_unlocks')
      .select('drop_id')
      .eq('user_id', userId)
      .then(({ data }) => {
        setUnlocked(new Set((data || []).map(u => u.drop_id)));
      });
  }, [userId]);

  const unlockDrop = async ({ dropId, userId: uid, amount }) => {
    const { error } = await supabase
      .from('d2f_drop_unlocks')
      .upsert({ drop_id: dropId, user_id: uid, amount_paid: amount }, { onConflict: 'drop_id,user_id' });
    if (!error) {
      setUnlocked(prev => new Set([...prev, dropId]));
      await supabase.from('d2f_drops').update({ unlock_count: supabase.rpc('increment', { x: 1 }) }).eq('id', dropId);
    }
    return { error };
  };

  return { unlocked, unlockDrop };
};